import React, { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { filter, size, some } from 'lodash'
import { ArrowLeft } from 'lucide-react'

import { Button, TOAST_OPTIONS, triggerToast } from '@/components'
import {
  AttachedFilesTable,
  backgroundUploadManager,
  BottomActionBar,
  CancelConfirmDialog,
  DeliverableSetSelect,
  FileUploadDropzone,
  UploadFile,
  useConfirmUpload,
  useFileUploadFiles,
  useFileUploadStore,
  useNavigationBlockContext,
  useRequestUploadUrls,
  useUploadContext,
} from '@/features/file-upload'
import {
  getNewFileVersionStatus,
  getNewVersionStatus,
} from '@/features/file-upload/utils'
import {
  DocumentType,
  FileValidationStatus,
  UploadConfirmation,
} from '@/schema'
import { chunkArray, formatNumber, timeout } from '@/utils/common'
import {
  MAX_CONCURRENT_UPLOADS,
  UPLOAD_CONFIRM_SIZE,
  UPLOAD_DELAY_MS,
  UPLOAD_FILE_BATCH_SIZE,
} from '@/utils/constants'
import { ROUTES } from '@/utils/routes'

const MODE_READONLY = 'readonly'
const MODE_EDITABLE = 'editable'

const UploadPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams<{ id: string }>()

  // Get navigation context
  const {
    showCancelDialog: contextShowCancelDialog,
    pendingNavigation,
    confirmNavigation,
    cancelNavigation,
  } = useNavigationBlockContext()

  // Get state and actions from Zustand store
  const files = useFileUploadFiles()
  const {
    selectedDeliverableSet,
    isUploading,
    error,
    referrerRoute,
    setContextDeliverableSetId,
    updateFile,
    removeFailedAndCancelledFiles,
    startUpload,
    completeUpload,
    setError,
    clearError,
    reset,
    setUploadRoutePath,
    setReferrerRoute,
  } = useFileUploadStore()

  const { mutateAsync: requestUploadUrls } = useRequestUploadUrls()
  const { mutateAsync: confirmUpload } = useConfirmUpload()
  const { registerFile, clearFileReferences } = useUploadContext()

  // Use context dialog state or local state for backward compatibility
  const [localShowCancelDialog, setLocalShowCancelDialog] =
    React.useState(false)
  const [dialogVariant, setDialogVariant] = React.useState<
    'cancel' | 'exit' | null
  >(null)
  const showCancelDialog = contextShowCancelDialog || localShowCancelDialog

  const mode = useMemo(() => {
    if (id) {
      return MODE_READONLY
    }
    return MODE_EDITABLE
  }, [id])

  useEffect(() => {
    setUploadRoutePath(globalThis.location.pathname)

    const from = (location.state as { from?: string })?.from
    if (from) {
      setReferrerRoute(from)
    } else {
      const currentPath = globalThis.location.pathname
      let calculatedReferrer: string = ROUTES.DELIVERABLE_SET

      if (currentPath.includes('/document/upload')) {
        calculatedReferrer = currentPath.replace('/upload', '')
      } else if (id) {
        calculatedReferrer = `/deliverable-set/${id}`
      }

      setReferrerRoute(calculatedReferrer)
    }

    if (id) {
      useFileUploadStore.getState().setSelectedDeliverableSet(null)
      setContextDeliverableSetId(id)
    } else {
      setContextDeliverableSetId(null)
      // Don't clear selectedDeliverableSet when returning to upload page during an active upload
      // This preserves the deliverable set data when navigating back from the floating indicator
      if (!isUploading) {
        useFileUploadStore.getState().setSelectedDeliverableSet(null)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isUploading])

  const backRoute = useMemo(() => {
    const currentPath = globalThis.location.pathname

    if (currentPath.includes('/document/upload')) {
      return currentPath.replace('/upload', '')
    }

    if (id) {
      return `/deliverable-set/${id}`
    }

    return ROUTES.DELIVERABLE_SET
  }, [id])

  // Helper to request upload URLs for files without API data
  const getAllUploadUrlData = useCallback(
    async (
      filesWithApiData: UploadFile[],
      filesWithoutApiData: UploadFile[]
    ) => {
      const allUploadUrlData: Array<{
        file: UploadFile
        uploadUrlData: {
          file_index: number
          document_id: string
          version_number: number
          is_initial: boolean
          blob_name: string
          blob_url: string
        }
      }> = []

      filesWithApiData.forEach((file) => {
        if (
          file.blobUrl &&
          file.documentId !== undefined &&
          file.fileIndex !== undefined &&
          file.versionNumber !== undefined &&
          file.isInitial !== undefined &&
          file.blobName
        ) {
          allUploadUrlData.push({
            file,
            uploadUrlData: {
              file_index: file.fileIndex,
              document_id: file.documentId,
              version_number: file.versionNumber,
              is_initial: file.isInitial,
              blob_name: file.blobName,
              blob_url: file.blobUrl,
            },
          })
        }
      })

      if (filesWithoutApiData.length > 0) {
        const fileBatches = chunkArray(
          filesWithoutApiData,
          UPLOAD_FILE_BATCH_SIZE
        )

        for (const batch of fileBatches) {
          const uploadRequest = {
            deliverable_set_id: selectedDeliverableSet!.id,
            files: batch.map((file) => ({
              document_type: file.documentType || 'other',
              filename: file.fileName,
              document_id: file.documentId || null,
            })),
          }

          const uploadResponse = await requestUploadUrls(uploadRequest)

          await timeout(UPLOAD_DELAY_MS)

          const containerData = uploadResponse.data[0]
          if (!containerData?.upload_urls) {
            throw new Error(t('fileUpload.errors.noUploadUrl'))
          }

          batch.forEach((file, index) => {
            const uploadUrlData = containerData.upload_urls[index]
            if (uploadUrlData) {
              allUploadUrlData.push({ file, uploadUrlData })
              updateFile(file.id, {
                documentId: uploadUrlData.document_id,
                fileIndex: uploadUrlData.file_index,
                versionNumber: uploadUrlData.version_number,
                isInitial: uploadUrlData.is_initial,
                versionStatus: uploadUrlData.is_initial
                  ? getNewFileVersionStatus()
                  : getNewVersionStatus(),
                blobName: uploadUrlData.blob_name,
                blobUrl: uploadUrlData.blob_url,
                validationApiCalled: true,
              })
            } else {
              updateFile(file.id, {
                validationStatus: FileValidationStatus.FAILED,
                errorMessage: t('fileUpload.errors.noUploadUrl'),
              })
            }
          })
        }
      }

      return allUploadUrlData
    },
    [selectedDeliverableSet, requestUploadUrls, updateFile, t]
  )

  // Helper to upload files with concurrency
  const uploadWithConcurrency = useCallback(
    async (
      allUploadUrlData: Array<{
        file: UploadFile
        uploadUrlData: {
          file_index: number
          document_id: string
          version_number: number
          is_initial: boolean
          blob_name: string
          blob_url: string
        }
      }>,
      confirmations: UploadConfirmation[]
    ) => {
      const totalFiles = allUploadUrlData.length
      let completedCount = 0
      const uploadQueue = [...allUploadUrlData]
      const activeUploads: Promise<void>[] = []

      const processNextUpload = async (): Promise<void> => {
        if (uploadQueue.length === 0) return

        const { file, uploadUrlData } = uploadQueue.shift()!
        registerFile(file.id, file.file)

        try {
          const confirmation = await backgroundUploadManager.uploadFile(
            file.id,
            file.file,
            uploadUrlData.blob_url,
            uploadUrlData.document_id,
            uploadUrlData.file_index,
            uploadUrlData.version_number,
            (file.documentType as DocumentType) || 'other'
          )

          if (confirmation) {
            confirmations.push(confirmation as UploadConfirmation)
          }

          completedCount++

          if (completedCount < totalFiles) {
            await timeout(UPLOAD_DELAY_MS)
          }
        } catch {
          completedCount++
        }

        if (uploadQueue.length > 0) {
          return processNextUpload()
        }
      }

      for (let i = 0; i < Math.min(MAX_CONCURRENT_UPLOADS, totalFiles); i++) {
        activeUploads.push(processNextUpload())
      }

      await Promise.all(activeUploads)
    },
    [registerFile]
  )

  /**
   * Handle confirm upload in chunks of UPLOAD_CONFIRM_SIZE
   * @param confirmations - Upload confirmations
   * @param deliverableSetId - Deliverable set ID
   */
  const handleConfirmUpload = useCallback(
    async (confirmations: UploadConfirmation[], deliverableSetId: string) => {
      if (confirmations.length > 0) {
        const confirmationChunks = chunkArray(
          confirmations,
          UPLOAD_CONFIRM_SIZE
        )

        for (const confirmations of confirmationChunks) {
          await confirmUpload({
            deliverable_set_id: deliverableSetId,
            confirmations,
          })
        }
      }
    },
    [confirmUpload]
  )

  const handleUpload = useCallback(async () => {
    if (!selectedDeliverableSet) {
      setError(t('fileUpload.validation.deliverableSetRequired'))
      return
    }

    const readyFiles = filter(
      files,
      (file) =>
        file.validationStatus === FileValidationStatus.READY_TO_UPLOAD ||
        (file.validationStatus === FileValidationStatus.ACTION_REQUIRED &&
          file.documentType)
    ) as UploadFile[]

    if (size(readyFiles) === 0) {
      setError(t('fileUpload.validation.noFilesToUpload'))
      return
    }

    try {
      clearError()
      removeFailedAndCancelledFiles()
      startUpload()
      navigate(referrerRoute || backRoute)

      const filesWithApiData = readyFiles.filter(
        (f) =>
          f.validationApiCalled &&
          f.blobUrl &&
          f.documentId &&
          f.fileIndex !== undefined &&
          f.versionNumber !== undefined &&
          f.blobName
      )
      const filesWithoutApiData = readyFiles.filter(
        (f) =>
          !f.validationApiCalled ||
          !f.blobUrl ||
          !f.documentId ||
          f.fileIndex === undefined ||
          f.versionNumber === undefined ||
          !f.blobName
      )

      // PHASE 1: Request upload URLs
      const allUploadUrlData = await getAllUploadUrlData(
        filesWithApiData,
        filesWithoutApiData
      )

      // PHASE 2: Upload files in parallel with max concurrency
      const confirmations: UploadConfirmation[] = []
      await uploadWithConcurrency(allUploadUrlData, confirmations)

      // PHASE 3: Confirm upload completion (paged by UPLOAD_CONFIRM_SIZE)
      await handleConfirmUpload(confirmations, selectedDeliverableSet.id)

      completeUpload()
      clearFileReferences()

      // Show upload results
      const successCount = confirmations.length
      const totalCount = allUploadUrlData.length
      const failedCount = totalCount - successCount

      if (failedCount > 0) {
        const filesText = totalCount === 1 ? 'file' : 'files'
        const failedText = failedCount === 1 ? 'file' : 'files'
        triggerToast(
          'error',
          `Uploaded ${formatNumber(successCount)} of ${formatNumber(totalCount)} ${filesText}. ${formatNumber(failedCount)} ${failedText} failed.`,
          { ...TOAST_OPTIONS, position: 'bottom-center' }
        )
      } else {
        const filesText = successCount === 1 ? 'file' : 'files'
        triggerToast(
          'success',
          `Uploaded ${formatNumber(successCount)} ${filesText} successfully`,
          { ...TOAST_OPTIONS, position: 'bottom-center' }
        )
      }

      reset()

      if (globalThis.location.pathname.includes('/upload')) {
        navigate(referrerRoute || backRoute)
      }
    } catch (uploadError) {
      setError(
        uploadError instanceof Error
          ? uploadError.message
          : t('fileUpload.errors.uploadFailed')
      )
      completeUpload()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedDeliverableSet,
    files,
    removeFailedAndCancelledFiles,
    startUpload,
    completeUpload,
    clearError,
    setError,
    updateFile,
    requestUploadUrls,
    confirmUpload,
    reset,
    navigate,
    referrerRoute,
    backRoute,
    t,
    registerFile,
    clearFileReferences,
    getAllUploadUrlData,
    uploadWithConcurrency,
    handleConfirmUpload,
  ])

  // Handle cancel button click
  const handleCancel = useCallback(() => {
    if (isUploading) {
      navigate(referrerRoute || backRoute)
      return
    }

    // Check if there are any files added
    const hasFiles = size(files) > 0

    if (hasFiles) {
      setDialogVariant('cancel')
      setLocalShowCancelDialog(true)
      return
    }

    reset()
    navigate(referrerRoute || backRoute)
  }, [isUploading, files, reset, navigate, referrerRoute, backRoute])

  // Handle confirmed cancel
  const handleConfirmCancel = useCallback(() => {
    backgroundUploadManager.cancelAllUploads()
    clearFileReferences()
    reset()

    // Navigate to the target path
    const targetPath = pendingNavigation || referrerRoute || backRoute
    navigate(targetPath)

    // Close both dialogs
    setLocalShowCancelDialog(false)
    if (contextShowCancelDialog) {
      confirmNavigation()
    }
  }, [
    reset,
    navigate,
    pendingNavigation,
    referrerRoute,
    backRoute,
    clearFileReferences,
    contextShowCancelDialog,
    confirmNavigation,
  ])

  const isUploadDisabled = useMemo(() => {
    const actionRequiredFilesWithoutType = filter(
      files,
      (f) =>
        f.validationStatus === FileValidationStatus.ACTION_REQUIRED &&
        !f.documentType
    )
    const hasActionRequiredWithoutType =
      size(actionRequiredFilesWithoutType) > 0

    const hasValidating = some(
      files,
      (f) => f.validationStatus === FileValidationStatus.VALIDATING
    )
    const hasReadyToUpload = some(
      files,
      (f) => f.validationStatus === FileValidationStatus.READY_TO_UPLOAD
    )
    const hasActionRequiredWithType = some(
      files,
      (f) =>
        f.validationStatus === FileValidationStatus.ACTION_REQUIRED &&
        f.documentType
    )

    if (hasActionRequiredWithoutType && size(files) > 0) {
      setError(
        t('fileUpload.validation.actionRequiredFiles', {
          count: size(actionRequiredFilesWithoutType),
        })
      )
    } else if (
      !hasActionRequiredWithoutType &&
      error?.includes('Action Required')
    ) {
      clearError()
    }

    return (
      !selectedDeliverableSet ||
      size(files) === 0 ||
      (!hasReadyToUpload && !hasActionRequiredWithType) ||
      hasActionRequiredWithoutType ||
      hasValidating ||
      isUploading
    )
  }, [
    selectedDeliverableSet,
    files,
    isUploading,
    error,
    setError,
    clearError,
    t,
  ])

  const handleBack = useCallback(() => {
    if (isUploading) {
      navigate(referrerRoute || backRoute)
      return
    }

    // Check if there are any files added
    const hasFiles = size(files) > 0

    if (hasFiles) {
      setDialogVariant('exit')
      setLocalShowCancelDialog(true)
      return
    }

    navigate(referrerRoute || backRoute)
  }, [isUploading, files, referrerRoute, backRoute, navigate])

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 space-y-6 overflow-y-auto">
        <Button
          onClick={handleBack}
          variant="text"
          color="black"
          className="flex items-center gap-1 p-0 text-sm font-semibold text-brand-600 hover:text-brand-700"
          leadingIcon={<ArrowLeft size={20} />}
        >
          {t('common.back')}
        </Button>

        <div>
          <h1 className="m-0 text-2xl font-semibold text-primary-900">
            {t('fileUpload.page.title')}
          </h1>
        </div>

        <DeliverableSetSelect mode={mode} disabled={isUploading} />

        <FileUploadDropzone disabled={isUploading || !selectedDeliverableSet} />

        <AttachedFilesTable />
      </div>

      <BottomActionBar
        error={error}
        onCancel={handleCancel}
        onUpload={handleUpload}
        isUploading={isUploading}
        isUploadDisabled={isUploadDisabled}
      />

      <CancelConfirmDialog
        open={showCancelDialog}
        variant={contextShowCancelDialog ? 'exit' : dialogVariant || 'exit'}
        onCancel={handleConfirmCancel}
        onContinue={() => {
          setLocalShowCancelDialog(false)
          setDialogVariant(null)
          if (contextShowCancelDialog) {
            cancelNavigation()
          }
        }}
      />
    </div>
  )
}

export default UploadPage
